<script lang="ts">
import { getContentListIncludedCategory, getRecommendContentList } from '@/api/content';
import { CategoryConnectRecEntityDto } from '@/api/dto/category.dto';
import { ContentEntityDto, SearchContentListDto } from '@/api/dto/content.dto';
import ApocImageSet from '@/components/common/ApocImageSet.vue';
import MainContentsList from '@/components/layout/MainContentsList.vue';
import MainBanner from '@/components/layout/common/MainBanner.vue';
import MoreArrow from '@/components/layout/common/MoreArrow.vue';
import SubBanner from '@/components/layout/common/SubBanner.vue';
import AppConfig from '@/constants';
import { APP_ENV_TYPE } from '@/constants';
import { HEADER_TYPE, POPUP_MODE } from '@/stores/state-store';
import { initStore } from '@/stores/store-manager';
import { LOADING_TYPE, OPEN_TYPE_CODE, PARENT_CATEGORY_TYPE_CODE, ORDER_TYPE, PLAY_MAIN_CONTENT } from '@/types';
import { getApiClient } from '@/utils/apiClient';
import { formatNumberWithCommas, getQueryString, loadLocalData, removeJsonLd } from '@/utils/common-util';
import { getEmitter } from '@/utils/event-emitter-utils';
import { loadApiGauge } from '@/utils/utils';
import { defineComponent, onMounted, onUnmounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

export default defineComponent({
  name: 'Main',
  components: {
    MoreArrow,
    ApocImageSet,
    MainContentsList,
    MainBanner,
    SubBanner,
  },
  setup() {
    const { t } = useI18n({ useScope: 'global' });
    const isMore = ref<boolean>(false);
    const apocContents = ref<CategoryConnectRecEntityDto[]>([]);
    const verticalContents = ref<CategoryConnectRecEntityDto[]>([]);
    const popVerticalContents = ref<CategoryConnectRecEntityDto[]>([]);
    const popularContents = ref<CategoryConnectRecEntityDto[]>([]);
    const bestContents = ref<CategoryConnectRecEntityDto[]>([]);
    const newestContents = ref<CategoryConnectRecEntityDto[]>([]);
    const isNewestContentsLoading = ref<boolean>(false);
    const isPopularContentsLoading = ref<boolean>(false);
    const isVerticalContentsLoading = ref<boolean>(false);
    const isBestContentsLoading = ref<boolean>(false);
    const isApocContentLoading = ref<boolean>(false);
    const router = useRouter();
    const storeManager = initStore();
    const apiClient = getApiClient(AppConfig.API_SERVER, storeManager);
    const totalSearchContentsCount = ref<number>(0);
    const keyword = ref<string | undefined>(getQueryString('keyword') ? getQueryString('keyword') : '');
    const isContentHeaderVisible = ref<boolean>(true);
    const headerElement = ref<HTMLElement | null>(null);
    const lateLoadTimer = ref<ReturnType<typeof setTimeout>>(); // api load가 늦은 경우 작동하는 타이머
    const lang = loadLocalData(AppConfig.KEYS.CONST.CHANGE_LANG) || 'KO';

    // palm, palate, pinky 순서
    const filterUserList =
      AppConfig.ENV === APP_ENV_TYPE.DEV
        ? ['e93b739d-6cc7-4bc2-8c5d-0f87c71737e4', 'ce740238-7bb5-423a-80ec-bda462266c06', 'd47205db-a436-4923-a9df-d0df517916f6']
        : AppConfig.ENV === APP_ENV_TYPE.PROD || AppConfig.ENV === APP_ENV_TYPE.STAGE
          ? ['e93b739d-6cc7-4bc2-8c5d-0f87c71737e4', '428be346-0928-4887-bd30-28e67ea7d1f6', '2cdf61e3-f718-4f9f-ba84-5a126aaf8e1e']
          : [];

    /**
     * PopularContents
     * CES 대비 STAGE에서는 고정된 콘텐츠가 노출되게 끔 작업
     * */
    const getPopularBestContents = () => {
      const param = new ContentEntityDto();
      param.type = PLAY_MAIN_CONTENT.PLAY_MADEAPOC;
      storeManager.dataStore.addPageApiTotalCount(1);
      getRecommendContentList(apiClient, param)
        .then(async (res) => {
          storeManager.dataStore.addLoadedCount(1);
          loadApiGauge(LOADING_TYPE.ROLL, '', '');
          if (res.resultCode !== 0) {
            console.error(res);
            window.alert(t('msg.RESULT_INVALID_PARAM'));
          } else {
            if (res.data) {
              bestContents.value = res.data as CategoryConnectRecEntityDto[];
              isBestContentsLoading.value = true;
            }
          }
        })
        .catch(() => {
          isBestContentsLoading.value = true;
          storeManager.dataStore.addLoadedCount(1);
          loadApiGauge(LOADING_TYPE.ROLL, '', '');
        });
    };

    /**
     * 이달의 콘텐츠
     * 콘텐츠 중 조회수 높은 것 순
     */
    const getPopularContents = async () => {
      const searchParam = new SearchContentListDto();
      searchParam.orderType = ORDER_TYPE.THREE_MONTH;
      searchParam.page = 1;
      searchParam.rows = 10;
      searchParam.openTypeCode = OPEN_TYPE_CODE.PUBLIC;
      searchParam.likeCategoryCode = PARENT_CATEGORY_TYPE_CODE.APC;
      storeManager.dataStore.addPageApiTotalCount(1);
      await getContentListIncludedCategory(apiClient, searchParam)
        .then(async (res) => {
          storeManager.dataStore.addLoadedCount(1);
          loadApiGauge(LOADING_TYPE.ROLL, '', '');
          if (res.resultCode !== 0) {
            console.error(res);
            window.alert(t('msg.RESULT_INVALID_PARAM'));
          } else {
            if (res.data) {
              popularContents.value = res.data as CategoryConnectRecEntityDto[];
              isPopularContentsLoading.value = true;
            }
          }
        })
        .catch(() => {
          isPopularContentsLoading.value = true;
          storeManager.dataStore.addLoadedCount(1);
          loadApiGauge(LOADING_TYPE.ROLL, '', '');
        });
    };

    /**
     * 최신 콘텐츠
     */
    const getNewestContents = async () => {
      const searchParam = new SearchContentListDto();
      searchParam.orderType = ORDER_TYPE.NEW;
      searchParam.page = 1;
      searchParam.rows = 12;
      searchParam.openTypeCode = OPEN_TYPE_CODE.PUBLIC;
      searchParam.likeCategoryCode = PARENT_CATEGORY_TYPE_CODE.APC;
      storeManager.dataStore.addPageApiTotalCount(1);
      await getContentListIncludedCategory(apiClient, searchParam)
        .then((res) => {
          storeManager.dataStore.addLoadedCount(1);
          loadApiGauge(LOADING_TYPE.ROLL, '', '');
          if (res.resultCode === 0) {
            if (res.data) {
              if (newestContents.value.length > 0) {
                const seen = new Set<string>();
                newestContents.value = [...newestContents.value, ...res.data].filter((item) => {
                  if (seen.has(item.contentIdx)) return false;
                  seen.add(item.contentIdx);
                  return true;
                });
              } else newestContents.value = res.data;
            }
            isNewestContentsLoading.value = true;
          }
        })
        .catch(() => {
          isNewestContentsLoading.value = true;
          storeManager.dataStore.addLoadedCount(1);
          loadApiGauge(LOADING_TYPE.ROLL, '', '');
        });
    };

    /**
     * 최신 콘텐츠 - 핑키 / 팔레트 / 팜 계정으로 만들어진 컨텐츠 조회
     */
    const getNewestContentsFilteredByUser = async () => {
      const searchParam = new SearchContentListDto();
      searchParam.orderType = ORDER_TYPE.NEW;
      searchParam.page = 1;
      searchParam.rows = 7;
      searchParam.regrUserIdxList = JSON.stringify(filterUserList);
      searchParam.openTypeCode = OPEN_TYPE_CODE.PUBLIC;
      searchParam.likeCategoryCode = PARENT_CATEGORY_TYPE_CODE.APC;
      storeManager.dataStore.addPageApiTotalCount(1);

      await getContentListIncludedCategory(apiClient, searchParam)
        .then((res) => {
          storeManager.dataStore.addLoadedCount(1);
          loadApiGauge(LOADING_TYPE.ROLL, '', '');
          if (res.resultCode === 0) {
            if (res.data) {
              if (newestContents.value.length > 0) {
                const seen = new Set<string>();
                newestContents.value = [...newestContents.value, ...res.data].filter((item) => {
                  if (seen.has(item.contentIdx)) return false;
                  seen.add(item.contentIdx);
                  return true;
                });
              } else newestContents.value = res.data;
            }
            isNewestContentsLoading.value = true;
          }
        })
        .catch(() => {
          isNewestContentsLoading.value = true;
          storeManager.dataStore.addLoadedCount(1);
          loadApiGauge(LOADING_TYPE.ROLL, '', '');
        });
    };

    /**
     * 컨텐츠
     */
    const getCollabContentList = async () => {
      const param = new ContentEntityDto();
      param.type = PLAY_MAIN_CONTENT.PLAY_COLLAB;
      storeManager.dataStore.addPageApiTotalCount(1);
      await getRecommendContentList(apiClient, param)
        .then(async (res) => {
          storeManager.dataStore.addLoadedCount(1);
          loadApiGauge(LOADING_TYPE.ROLL, '', '');
          if (res.resultCode !== 0) {
            console.error(res);
            window.alert(t('msg.RESULT_INVALID_PARAM'));
          } else {
            if (res.data) {
              // popularContents.value = res.data as CategoryConnectRecEntityDto[];
              popularContents.value = [...(res.data as CategoryConnectRecEntityDto[])].sort(() => Math.random() - 0.5);

              isPopularContentsLoading.value = true;
            }
          }
        })
        .catch(() => {
          isPopularContentsLoading.value = true;
          storeManager.dataStore.addLoadedCount(1);
          loadApiGauge(LOADING_TYPE.ROLL, '', '');
        });
    };

    /**
     * apoc 콜라보 콘텐츠
     */
    const getPopVerticalContentList = async () => {
      const param = new ContentEntityDto();
      param.type = PLAY_MAIN_CONTENT.PLAY_POP_VRTC;
      storeManager.dataStore.addPageApiTotalCount(1);
      await getRecommendContentList(apiClient, param)
        .then(async (res) => {
          storeManager.dataStore.addLoadedCount(1);
          loadApiGauge(LOADING_TYPE.ROLL, '', '');
          if (res.resultCode !== 0) {
            console.error(res);
            window.alert(t('msg.RESULT_INVALID_PARAM'));
          } else {
            if (res.data) {
              popVerticalContents.value = res.data as CategoryConnectRecEntityDto[];
              isPopularContentsLoading.value = true;
            }
          }
        })
        .catch(() => {
          isPopularContentsLoading.value = true;
          storeManager.dataStore.addLoadedCount(1);
          loadApiGauge(LOADING_TYPE.ROLL, '', '');
        });
    };

    /**
     * 새로형 콘텐츠
     */
    const getVerticalContentList = async () => {
      const param = new ContentEntityDto();
      param.type = PLAY_MAIN_CONTENT.PLAY_VRTC;
      storeManager.dataStore.addPageApiTotalCount(1);
      await getRecommendContentList(apiClient, param)
        .then(async (res) => {
          storeManager.dataStore.addLoadedCount(1);
          loadApiGauge(LOADING_TYPE.ROLL, '', '');
          if (res.resultCode !== 0) {
            console.error(res);
            window.alert(t('msg.RESULT_INVALID_PARAM'));
          } else {
            if (res.data) {
              verticalContents.value = res.data as CategoryConnectRecEntityDto[];
              isVerticalContentsLoading.value = true;
            }
          }
        })
        .catch(() => {
          isVerticalContentsLoading.value = true;
          storeManager.dataStore.addLoadedCount(1);
          loadApiGauge(LOADING_TYPE.ROLL, '', '');
        });
    };

    /**
     * made by apoc 컨텐츠
     */
    const getWellMadeContentList = async () => {
      const param = new ContentEntityDto();
      param.type = PLAY_MAIN_CONTENT.PLAY_MADEAPOC;
      storeManager.dataStore.addPageApiTotalCount(1);
      await getRecommendContentList(apiClient, param)
        .then(async (res) => {
          storeManager.dataStore.addLoadedCount(1);
          loadApiGauge(LOADING_TYPE.ROLL, '', '');
          if (res.resultCode !== 0) {
            console.error(res);
            window.alert(t('msg.RESULT_INVALID_PARAM'));
          } else {
            if (res.data) {
              apocContents.value = res.data as CategoryConnectRecEntityDto[];
              isApocContentLoading.value = true;
            }
          }
        })
        .catch(() => {
          isApocContentLoading.value = true;
          storeManager.dataStore.addLoadedCount(1);
          loadApiGauge(LOADING_TYPE.ROLL, '', '');
        });
    };

    // 모든 콘텐츠 다시 불러오기
    const resetData = async () => {
      await getPopVerticalContentList();
      await getCollabContentList();
      // await getPopularBestContents();
      await getNewestContentsFilteredByUser();
      await getNewestContents();
      await getWellMadeContentList();
      await getVerticalContentList();
    };

    // 더보기 설정
    const seeMore = (orderType: string, likeCategoryCode?: string) => {
      storeManager.dataStore.setOrderType(orderType);
      if (likeCategoryCode) storeManager.dataStore.setLikeCategoryCode(likeCategoryCode);
      router.push({
        path: '/search',
        query: {
          keyword: keyword.value,
          page: 1,
        },
      });
    };

    const handleScroll = () => {
      const scrollPosition = window.scrollY || document.documentElement.scrollTop;
      if (isContentHeaderVisible.value) {
        storeManager.stateStore.setHeaderVisible(true);
      } else {
        storeManager.stateStore.setHeaderVisible(false);
      }

      if (window.innerWidth <= 767) {
        //모바일
        isContentHeaderVisible.value = scrollPosition <= 0;
      } else if (window.innerWidth <= 1023) {
        //태블릿
        isContentHeaderVisible.value = scrollPosition <= 0;
      } else {
        // 컴퓨터
        isContentHeaderVisible.value = scrollPosition <= 0;
      }
      setThemeColor();
    };

    const scrollIntoView = () => {
      if (headerElement.value) {
        headerElement.value.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
    };

    // 반응형으로 요소의 높이가 변경되는 경우, resize 이벤트를 감지하여 scrollIntoView() 호출
    const handleResize = () => {
      scrollIntoView();
    };

    /**
     * 테마 색깔 설정
     *
     * */
    const setThemeColor = () => {
      const theme = document.getElementById('theme-color') as HTMLMetaElement;
      if (theme) {
        if (isContentHeaderVisible.value) {
          theme.content = '#8976FD';
        } else {
          theme.content = '#FFFFFF';
        }
      }
    };

    /**
     * 페이지를 벗어나는 경우 테마를 초기 값으로 재설정
     * */
    const initThemeColor = () => {
      const theme = document.getElementById('theme-color') as HTMLMetaElement;
      if (theme) theme.content = '#FFFFFF';
    };

    /**
     * 로딩바 활성화를 위한 타이머를 생성
     */
    const initLateLoadTimer = () => {
      lateLoadTimer.value = setTimeout(() => {
        if (storeManager.dataStore.loadedCount < storeManager.dataStore.pageApiTotalCount) {
          // 현재 로드 되어야 하는 전체 API 개수 초기화 (전체 - 현재 로드된 갯수)
          const nowApiCount = storeManager.dataStore.pageApiTotalCount - storeManager.dataStore.loadedCount;
          storeManager.dataStore.setPageApiTotalCount(nowApiCount);
          // 현재 로드된 API 갯수 초기화
          storeManager.dataStore.setLoadedCount(0);
          storeManager.stateStore.loadingState = true;
        }
      }, 3000);
    };

    const setJsonLd = () => {
      const jsonLdData = {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: `${t('seo.title')}`,
        description: `${t('seo.description')}`,
        url: `${AppConfig.FRONT_HOST}`,
      };

      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.innerHTML = JSON.stringify(jsonLdData);
      document.head.appendChild(script);
    };

    onMounted(() => {
      getEmitter().emit('LOADING_STATE', {
        loadingType: LOADING_TYPE.NONE,
        des: '',
        title: '',
        progress: 0,
      });
      storeManager.stateStore.setHeaderState(HEADER_TYPE.DEFAULT); //헤더 상태값 변경
      storeManager.dataStore.setHeaderTitle(''); //헤더 타이틀 빈 문자열 생성
      initLateLoadTimer();
      resetData();
      window.addEventListener('scroll', handleScroll);
      window.addEventListener('resize', handleResize);
      storeManager.dataStore.setSearchKeyword('');
      setThemeColor();
      setJsonLd();
    });

    onUnmounted(async () => {
      if (lateLoadTimer.value) clearTimeout(lateLoadTimer.value);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
      initThemeColor();
      await removeJsonLd();
    });

    return {
      t,
      lang,
      isMore,
      keyword,
      seeMore,
      resetData,
      ORDER_TYPE,
      PARENT_CATEGORY_TYPE_CODE,
      isContentHeaderVisible,
      popularContents,
      newestContents,
      apocContents,
      totalSearchContentsCount,
      isNewestContentsLoading,
      isPopularContentsLoading,
      isVerticalContentsLoading,
      isApocContentLoading,
      formatNumberWithCommas,
      getWellMadeContentList,
      getPopularContents,
      getNewestContents,
      getVerticalContentList,
      verticalContents,
      popVerticalContents,
    };
  },
});
</script>

<template>
  <ClientOnly>
    <main-banner :model-value="keyword" />
    <div class="xr-banner-wrapper">
      <!-- <div class="xr-banner">
        <div class="img-slider">
          <img src="/assets/images/banner/xr-banner.webp" />
          <img src="/assets/images/banner/xr-banner.webp" />
        </div>
      </div> -->
      <!-- <div class="overlay-img">
        <apoc-image-set
          class="main-text-img"
          src="/assets/images/banner/main_banner_img.webp"
        />
      </div> -->
      <div class="xr-banner-title">
        Quick <strong>content.</strong><br class="br" />
        More <strong>stories.</strong>
      </div>
    </div>
    <div class="main-wrapper">
      <div class="main">
        
        <!-- 이달의 콘텐츠 -->
        <div class="title-wrapper">
          <div v-if="lang === 'ko'" class="apoc-made-wrapper">
            <span>이달의 </span>
            <strong>베스트 & 콜라보</strong>
          </div>
          <div v-else class="apoc-made-wrapper">
            <span>This Month's </span>
            <strong>Best & Collab</strong>
          </div>
        </div>
        <!-- PLAY_POP_VRTC -->
        <div class="content-vertical pop">
          <main-contents-list
            :is-loaded="isPopularContentsLoading"
            :data="popVerticalContents.slice(0, 5)"
            :is-vertical="true"
            :get-contents-list="getPopularContents"
          />
        </div>
        <div class="top-content-list">
          <main-contents-list
            :is-loaded="isPopularContentsLoading"
            :data="popularContents.slice(0, 4)"
            :is-common="false"
            :get-contents-list="getPopularContents"
          />
        </div>

        <!-- 최신 콘텐츠 -->
        <div class="title-wrapper">
          <i18n-t keypath="layout.listItem.content" tag="h2" scope="global">
            <template #contentTitle>
              <strong>{{ t("layout.listItem.newest") }}</strong>
            </template>
          </i18n-t>
          <MoreArrow
            :on-click="
              () => seeMore(ORDER_TYPE.NEW, PARENT_CATEGORY_TYPE_CODE.APC)
            "
          />
        </div>
        <div class="new-content-list">
          <main-contents-list
            :is-loaded="isNewestContentsLoading"
            :data="newestContents.slice(0, 11)"
            :get-contents-list="getNewestContents"
          />
        </div>
      </div>
      <sub-banner />
      <div class="main">

        <!-- 세로 콘텐츠 -->
        <div class="title-wrapper">
          <i18n-t keypath="layout.listItem.content" tag="h2" scope="global">
            <template #contentTitle>
              <strong>{{ t("layout.listItem.vertical") }}</strong>
            </template>
          </i18n-t>
        </div>
        <div class="content-vertical">
          <main-contents-list
            :is-loaded="isVerticalContentsLoading"
            :data="verticalContents.slice(0, 5)"
            :is-vertical="true"
            :get-contents-list="getVerticalContentList"
          />
        </div>

        <!-- 아폭에서 만든 컨텐츠 -->
        <div class="title-wrapper">
          <div v-if="lang === 'ko'" class="apoc-made-wrapper">
            <strong>아폭</strong>
            <span>에서 만든 콘텐츠</span>
          </div>
          <div v-else class="apoc-made-wrapper">
            <strong>Made by apoc</strong>
          </div>
        </div>
        <div class="made-by-apoc">
          <main-contents-list
            category-text="XR"
            :is-loaded="isApocContentLoading"
            :data="apocContents.slice(0, 7)"
            :get-contents-list="getWellMadeContentList"
          />
        </div>
      </div>
    </div>
    <div class="award-section">
       <!-- 손 아이콘 -->
      <div class='gesture-icon-wrapper'>
        <apoc-image-set class="header-arrow" src="/assets/images/main/love-you-gesture.gif" />
      </div>
      <!-- 타이틀 -->
       <div class="title-wrapper">
          <div>{{t('mainPageAward.titleFirst')}}</div>
          <div>{{t('mainPageAward.titleSec')}}</div>
      </div>
      <!-- 수상 경력 -->
      <div class="crown-list">
        <div class="crown-item-wrapper">
          <div class="crown-item">
            <div>Revolutionary<br>
              AI Education<br>Company</div>
            <div>at ASU+GSV Air Show<br>2024
            </div>
            </div>
        </div>
        <div class="crown-item-wrapper">
          <div class="crown-item">
            <div>Semi-finalist</div>
            <div>as AI Disruptor at<br>Supernova Challenge<br> 
            at Gitex Global 2023
            </div>
            </div>
        </div>
        <div class="crown-item-wrapper">
          <div class="crown-item">
            <div>Outstanding<br>company</div>
            <div>in GA sector<br>
              at Viva Tech 2023
            </div>
            </div>
        </div>
      </div>
      <!-- 로고 리스트 -->
      <div class="logo-list">
        <ul>
            <li><apoc-image-set class="header-arrow" src="/assets/images/main/samsung.webp" :imgSets="3"/></li>
            <li><apoc-image-set class="header-arrow" src="/assets/images/main/ces.webp" :imgSets="3"/></li>
            <li><apoc-image-set class="header-arrow" src="/assets/images/main/viva.webp" :imgSets="3"/></li>
            <li><apoc-image-set class="header-arrow" src="/assets/images/main/gitex.webp" :imgSets="3"/></li>
            <li><apoc-image-set class="header-arrow" src="/assets/images/main/south.webp" :imgSets="3"/></li>
            <li><apoc-image-set class="header-arrow" src="/assets/images/main/pen.webp" :imgSets="3"/></li>
            <li><apoc-image-set class="header-arrow" src="/assets/images/main/future.webp" :imgSets="3"/></li>
            <li><apoc-image-set class="header-arrow" src="/assets/images/main/asu.webp" :imgSets="3"/></li>
            <li><apoc-image-set class="header-arrow" src="/assets/images/main/sxsw.webp" :imgSets="3"/></li>
            <li><apoc-image-set class="header-arrow" src="/assets/images/main/alchemist.webp" :imgSets="3"/></li>
            <li><apoc-image-set class="header-arrow" src="/assets/images/main/mind.webp" :imgSets="3"/></li>
        </ul>
      </div>
    </div>
  </ClientOnly>
</template>
